import { useEffect, useRef } from "react"
import "./CosmosBackground.css"

const STAR_COUNT = 320

const NEBULAE = [
  {
    px: 0.12, py: 0.22, r: 0.42, pa: 0.13, ph: 0.0,
    ox: 0.15, oy: 0.10, osx: 9e-4,  osy: 7e-4,  oph: 0.0,
    rgb: [40, 60, 130], cAmp: [12, 15, 25], cSpd: [4e-4, 3e-4, 5e-4],   cPh: [0.0, 2.1, 4.3],
  },
  {
    px: 0.75, py: 0.13, r: 0.34, pa: 0.10, ph: 2.1,
    ox: 0.12, oy: 0.14, osx: 7e-4,  osy: 1.1e-3, oph: 1.5,
    rgb: [75, 20, 105], cAmp: [20, 8,  25], cSpd: [5e-4,  6e-4,  3.5e-4], cPh: [1.2, 0.5, 3.1],
  },
  {
    px: 0.50, py: 0.70, r: 0.46, pa: 0.14, ph: 4.2,
    ox: 0.18, oy: 0.12, osx: 1.2e-3, osy: 8e-4,  oph: 3.0,
    rgb: [15, 78, 52],  cAmp: [10, 20, 15], cSpd: [3.5e-4, 4.5e-4, 6e-4],  cPh: [2.5, 1.0, 0.0],
  },
  {
    px: 0.87, py: 0.60, r: 0.28, pa: 0.08, ph: 1.1,
    ox: 0.10, oy: 0.08, osx: 1.3e-3, osy: 9e-4,  oph: 4.5,
    rgb: [88, 42, 12],  cAmp: [18, 12, 8],  cSpd: [6e-4,   5e-4,   4e-4],   cPh: [3.5, 2.0, 5.0],
  },
  {
    px: 0.28, py: 0.85, r: 0.35, pa: 0.11, ph: 3.3,
    ox: 0.14, oy: 0.16, osx: 8e-4,  osy: 1.4e-3, oph: 2.2,
    rgb: [25, 48, 95],  cAmp: [10, 12, 30], cSpd: [4.5e-4, 3e-4,   5.5e-4], cPh: [4.0, 3.2, 1.8],
  },
]

function mkStar(W, H) {
  const tier = Math.random()
  const big  = tier > 0.96
  const mid  = tier > 0.86
  const r    = big  ? 1.8 + Math.random() * 0.9
             : mid  ? 0.9 + Math.random() * 0.7
             :        0.25 + Math.random() * 0.55
  const cr = Math.random()
  const color = cr > 0.90 ? "#b8ccff"
              : cr > 0.82 ? "#ffd8a0"
              :              "#ffffff"
  return {
    x:     Math.random() * W,
    y:     Math.random() * H,
    r,
    baseA: big ? 0.90 : mid ? 0.75 : 0.35 + Math.random() * 0.42,
    tAmp:  0.18 + Math.random() * 0.52,
    tPh:   Math.random() * Math.PI * 2,
    tSpd:  0.004 + Math.random() * 0.018,
    dx:    (Math.random() - 0.5) * 0.055,
    dy:    (Math.random() - 0.5) * 0.038,
    color,
    glow:  big,
  }
}

function CosmosBackground() {
  const canvasRef = useRef(null)

  useEffect(() => {
    const cvs = canvasRef.current
    if (!cvs) return
    const ctx = cvs.getContext("2d")
    let W = 0, H = 0
    let stars = []
    let t = 0
    let rafId = null
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    function resize() {
      W = cvs.width  = window.innerWidth
      H = cvs.height = window.innerHeight
    }

    function initStars() {
      stars = Array.from({ length: STAR_COUNT }, () => mkStar(W, H))
    }

    function draw() {
      ctx.clearRect(0, 0, W, H)

      for (const nb of NEBULAE) {
        const pulse = 1 + Math.sin(t * 8e-4 + nb.ph) * nb.pa
        const nx  = (nb.px + Math.sin(t * nb.osx + nb.oph) * nb.ox) * W
        const ny  = (nb.py + Math.cos(t * nb.osy + nb.oph) * nb.oy) * H
        const rad = nb.r * Math.min(W, H) * pulse
        const cr  = Math.round(Math.max(0, Math.min(255, nb.rgb[0] + Math.sin(t * nb.cSpd[0] + nb.cPh[0]) * nb.cAmp[0])))
        const cg  = Math.round(Math.max(0, Math.min(255, nb.rgb[1] + Math.sin(t * nb.cSpd[1] + nb.cPh[1]) * nb.cAmp[1])))
        const cb  = Math.round(Math.max(0, Math.min(255, nb.rgb[2] + Math.sin(t * nb.cSpd[2] + nb.cPh[2]) * nb.cAmp[2])))
        const g   = ctx.createRadialGradient(nx, ny, 0, nx, ny, rad)
        g.addColorStop(0,    `rgba(${cr},${cg},${cb},.13)`)
        g.addColorStop(0.45, `rgba(${cr},${cg},${cb},.06)`)
        g.addColorStop(0.80, `rgba(${cr},${cg},${cb},.015)`)
        g.addColorStop(1,    `rgba(${cr},${cg},${cb},0)`)
        ctx.fillStyle = g
        ctx.fillRect(0, 0, W, H)
      }

      for (const s of stars) {
        s.tPh += s.tSpd
        const a = Math.max(0.04, s.baseA * (1 - s.tAmp + s.tAmp * Math.sin(s.tPh)))
        s.x = ((s.x + s.dx) + W) % W
        s.y = ((s.y + s.dy) + H) % H
        ctx.globalAlpha = a
        if (s.glow) { ctx.shadowBlur = s.r * 5; ctx.shadowColor = s.color }
        ctx.beginPath()
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2)
        ctx.fillStyle = s.color
        ctx.fill()
        if (s.glow) ctx.shadowBlur = 0
      }

      ctx.globalAlpha = 1
      t++
      if (!reduced) rafId = requestAnimationFrame(draw)
    }

    resize()
    initStars()

    if (reduced) {
      draw()
    } else {
      draw()
      window.addEventListener("resize", resize)
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      window.removeEventListener("resize", resize)
    }
  }, [])

  return <canvas ref={canvasRef} className="cosmos-bg" aria-hidden="true" />
}

export default CosmosBackground
